import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const SimpleLocationPicker = ({ onLocationSelect }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null);

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          // Use a simple reverse geocoding service
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
          );
          const data = await response.json();
          
          let fullAddress = '';
          if (data.localityInfo && data.localityInfo.administrative) {
            const admin = data.localityInfo.administrative;
            fullAddress = `${admin[0]?.name || ''}, ${admin[1]?.name || ''}, ${data.locality || 'Jaipur'}, ${data.countryName || 'India'}`.replace(/^,\s*/, '');
          }
          
          if (!fullAddress) {
            fullAddress = `Location: ${lat.toFixed(4)}, ${lng.toFixed(4)}, Jaipur, India`;
          }

          setAddress(fullAddress);
          setCoordinates({ lat, lng });
          
          if (onLocationSelect) {
            onLocationSelect({
              address: fullAddress,
              lat,
              lng,
              coordinates: { lat, lng }
            });
          }
          
          toast.success('Location detected successfully!');
        } catch (error) {
          console.error('Error getting address:', error);
          toast.error('Could not get address. Please enter manually.');
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLoading(false);
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location access denied. Please enable location permissions.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information unavailable.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out.');
            break;
          default:
            toast.error('An unknown error occurred while detecting location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  return (
    <div style={{
      width: '100%',
      height: '400px',
      backgroundColor: '#f3f4f6',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '20px',
      position: 'relative'
    }}>
      {/* Instructions */}
      <div style={{
        position: 'absolute',
        top: '16px',
        left: '16px',
        right: '16px',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        textAlign: 'center',
        backdropFilter: 'blur(10px)',
        zIndex: 1000
      }}>
        📍 Simple Location Detection (Fallback)
      </div>

      {/* Map Icon */}
      <div style={{
        width: '80px',
        height: '80px',
        backgroundColor: '#ff6b35',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '32px'
      }}>
        🗺️
      </div>

      {/* Instructions */}
      <div style={{ textAlign: 'center', color: '#6b7280' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px', color: '#374151' }}>
          Location Detection
        </h3>
        <p style={{ fontSize: '14px', margin: '0 0 16px' }}>
          Click the button below to detect your current location
        </p>
      </div>

      {/* Detect Location Button */}
      <button
        onClick={detectLocation}
        disabled={isLoading}
        style={{
          padding: '12px 24px',
          backgroundColor: isLoading ? '#9ca3af' : '#ff6b35',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = '#e55a2b';
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = '#ff6b35';
          }
        }}
      >
        {isLoading ? (
          <>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid #f3f4f6',
              borderTop: '2px solid #ff6b35',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Detecting...
          </>
        ) : (
          <>
            📍 Detect My Location
          </>
        )}
      </button>

      {/* Address Display */}
      {address && (
        <div style={{
          position: 'absolute',
          bottom: '16px',
          left: '16px',
          right: '16px',
          backgroundColor: 'white',
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
            Detected Address:
          </div>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {address}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleLocationPicker;
